import React, { useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { MousePointerClick } from 'lucide-react'
import { MatchDetailsPreview } from './MatchDetailsPreview'
import { CommunityMatchPreview } from './CommunityMatchPreview'

const VIEWS = [
  { id: 'app', label: 'Mobile app', hint: 'tap the tabs, swipe and scroll' },
  { id: 'community', label: 'Web dashboard', hint: 'scroll the match page' },
]

const COMMUNITY_MAX_WIDTH = 440

export function phoneHeightFor(screenWidth) {
  return Math.round((852 * screenWidth) / 393) + 12
}

export function ProductPreview({ sport, screenWidth = 288 }) {
  const reduceMotion = useReducedMotion()
  const indicatorId = React.useId()
  const [view, setView] = useState('app')
  const [width, setWidth] = useState(0)
  const boxRef = useRef(null)
  const phoneHeight = phoneHeightFor(screenWidth)

  useEffect(() => {
    const node = boxRef.current
    if (!node || typeof ResizeObserver === 'undefined') return undefined
    const observer = new ResizeObserver(([entry]) => setWidth(Math.round(entry.contentRect.width)))
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  const active = VIEWS.find((v) => v.id === view)
  const communityWidth = Math.min(width || COMMUNITY_MAX_WIDTH, COMMUNITY_MAX_WIDTH)

  return (
    <figure ref={boxRef} className="m-0 flex w-full flex-col items-center">
      <div role="radiogroup" aria-label="Preview" className="mb-3 flex w-full max-w-[340px] rounded-full bg-white p-1 ring-1 ring-[#e7e5e4]">
        {VIEWS.map(({ id, label }) => {
          const selected = view === id
          return (
            <button
              key={id}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => setView(id)}
              className={`relative min-h-[36px] flex-1 whitespace-nowrap rounded-full px-2 text-[13px] font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0c3a42] ${
                selected ? 'text-white' : 'text-[#57534e] hover:text-[#1c1917]'
              }`}
            >
              {selected ? (
                <motion.span
                  layoutId={reduceMotion ? undefined : `preview-view-${indicatorId}`}
                  className="absolute inset-0 rounded-full bg-[#071018]"
                  transition={{ duration: 0.25, ease: [0.25, 1, 0.5, 1] }}
                />
              ) : null}
              <span className="relative">{label}</span>
            </button>
          )
        })}
      </div>

      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={view}
          initial={{ opacity: 0, y: reduceMotion ? 0 : 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: reduceMotion ? 0 : -6 }}
          transition={{ duration: 0.22, ease: [0.25, 1, 0.5, 1] }}
        >
          {view === 'app' ? (
            <MatchDetailsPreview sport={sport} screenWidth={screenWidth} />
          ) : (
            <CommunityMatchPreview sport={sport} width={communityWidth} height={phoneHeight - 26} />
          )}
        </motion.div>
      </AnimatePresence>

      <figcaption className="mt-3 flex items-center justify-center gap-2 rounded-full bg-[#14B8A6]/10 px-3.5 py-2 text-[13px] text-[#0f3f3a]">
        <MousePointerClick aria-hidden className="h-4 w-4 shrink-0 text-[#0d9488]" />
        <span>
          <span className="font-semibold">Try it:</span> {active.hint}.
        </span>
      </figcaption>
    </figure>
  )
}
